/* ========= Wassla Safe Share (MVP) =========
   - Manual share only (NO auto posting)
   - NO bulk posting
   - NO group scraping
   - Uses native share sheet (mobile) + copy link fallback
   - Optional pre-written share text
=========================================== */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Copy, Link2, Check, FileText, MessageCircle, Users, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import { buildShareUrl, shareNative } from '../utils/shareUtils';
import './AdShare.css';

/**
 * iter312 — Build share text in the Wassla canonical "Promote this Ad"
 * format. Exact copy follows the operator spec:
 *
 *   AR: شوف الإعلان دا في وصلة:
 *       {title}
 *       السعر: {price}
 *       الموقع: {location}
 *       الرابط: {url}
 *
 *       وصلة — رابط واحد، فرص متعددة
 *
 *   EN: Check this listing on Wassla:
 *       {title}
 *       Price: {price}
 *       Location: {location}
 *       Link: {url}
 *
 *       Wassla — One link, many opportunities
 *
 * Fields with empty values are omitted (no "Price:" empty line). The
 * `contactLine` prop, if supplied, is appended above the link for
 * backwards-compat with earlier callers.
 *
 * @param {Function} t - Translation function from react-i18next
 */
function buildShareText({ title, price, city, country, contactLine, url, isRTL, t }) {
  const lines = [];
  lines.push(
    isRTL ? 'شوف الإعلان دا في وصلة:' : 'Check this listing on Wassla:'
  );
  if (title) lines.push(String(title));
  if (price) {
    lines.push(`${isRTL ? 'السعر' : 'Price'}: ${price}`);
  }
  const loc = [city, country].filter(Boolean).join(' - ');
  if (loc) {
    lines.push(`${isRTL ? 'الموقع' : 'Location'}: ${loc}`);
  }
  if (contactLine) lines.push(String(contactLine));
  if (url) {
    lines.push(`${isRTL ? 'الرابط' : 'Link'}: ${url}`);
  }
  lines.push(''); // blank line before brand CTA
  lines.push(
    isRTL ? 'وصلة — رابط واحد، فرص متعددة' : 'Wassla — One link, many opportunities'
  );
  return lines.join('\n');
}

/**
 * Copy text to clipboard with fallback
 */
async function copyToClipboard(text) {
  // Try modern clipboard API first (requires HTTPS or localhost)
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      console.warn('Clipboard API failed, trying fallback:', e);
    }
  }
  
  // Fallback for older browsers or when clipboard API fails
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    ta.setAttribute('readonly', ''); // Prevent keyboard on mobile
    document.body.appendChild(ta);
    
    // Handle iOS
    const range = document.createRange();
    range.selectNodeContents(ta);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    ta.setSelectionRange(0, text.length); // For mobile
    
    const success = document.execCommand('copy');
    document.body.removeChild(ta);
    
    if (!success) {
      throw new Error('execCommand copy failed');
    }
    return true;
  } catch (e) {
    console.error('Clipboard fallback failed:', e);
    throw new Error('Could not copy to clipboard');
  }
}

/**
 * AdShare Component
 * 
 * @param {Object} props
 * @param {string} props.adId - Ad ID
 * @param {string} props.title - Ad title
 * @param {string} [props.price] - Ad price
 * @param {string} [props.city] - City
 * @param {string} [props.country] - Country
 * @param {string} [props.imageUrl] - Image URL for previews
 * @param {string} [props.publicUrl] - Custom public URL
 * @param {string} [props.contactLine] - Contact info text
 * @param {'compact' | 'full'} [props.variant='full'] - Display variant
 */
export function AdShare({
  adId,
  title,
  price,
  city,
  country,
  imageUrl,
  publicUrl,
  contactLine,
  variant = 'full'
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const [status, setStatus] = useState('');
  const [copied, setCopied] = useState(null); // 'link' | 'text' | null

  // Build the ad URL — always on the public domain (never the Capacitor
  // localhost origin) and with the /#/ hash prefix the router requires.
  const url = useMemo(() => {
    if (publicUrl) return publicUrl;
    return buildShareUrl(`/ad/${adId}`);
  }, [publicUrl, adId]);

  // Build share text
  const shareText = useMemo(
    () =>
      buildShareText({
        title,
        price,
        city,
        country,
        contactLine,
        url,
        isRTL,
        t
      }),
    [title, price, city, country, contactLine, url, isRTL, t]
  );

  // Track share event (optional analytics)
  async function trackShare(source) {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adId, source }),
      });
    } catch {
      // Ignore tracking failures
    }
  }

  // Native share handler
  async function onNativeShare() {
    setStatus('');
    setCopied(null);

    // Native share sheet (Android/iOS via Capacitor, browsers via Web Share)
    const res = await shareNative({ title, text: shareText, url });
    if (res.completed) {
      if (res.method !== 'canceled') {
        await trackShare(res.method === 'native' ? 'native' : 'web_share');
        toast.success(t('toast.sharedSuccess'));
      }
      return;
    }

    // Fallback: copy link
    try {
      await copyToClipboard(url);
      await trackShare('copy_link');
      setCopied('link');
      toast.success(t('toast.linkCopied'));
    } catch {
      setStatus('');
    }
  }

  // Copy link handler
  async function onCopyLink() {
    setStatus('');
    setCopied(null);
    
    try {
      await copyToClipboard(url);
      await trackShare('copy_link');
      setCopied('link');
      toast.success(t('toast.linkCopied'));
      
      // Reset after 2 seconds
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  }

  // Copy full text handler
  async function onCopyText() {
    setStatus('');
    setCopied(null);
    
    try {
      await copyToClipboard(shareText);
      await trackShare('copy_text');
      setCopied('text');
      toast.success(t('toast.shareTextCopied'));
      
      // Reset after 2 seconds
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  }

  // iter312 — WhatsApp Status / Group share.
  // Mobile native share sheet (where available) lets the user pick
  // WhatsApp → Status / a Group / a Contact. We try it first because
  // it gives the cleanest UX. On unsupported browsers we fall back to
  // the universal `wa.me/?text=` deep link which opens WhatsApp with
  // the pre-filled message and lets the user choose the destination.
  //
  // CRITICAL — Per operator policy:
  //   • No auto-posting. The user always picks the destination.
  //   • No promise of guaranteed Status placement (we cannot force it).
  //   • No group scraping.
  async function onShareWhatsAppStatus() {
    setStatus('');
    setCopied(null);
    const res = await shareNative({ title, text: shareText, url });
    if (res.completed) {
      if (res.method !== 'canceled') {
        await trackShare('whatsapp_status_native');
        toast.success(t('toast.sharedSuccess'));
      }
      return;
    }
    // Fallback: open WhatsApp deep link.
    try {
      const link = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(link, '_blank', 'noopener,noreferrer');
      await trackShare('whatsapp_status_link');
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  }

  async function onShareWhatsAppGroup() {
    setStatus('');
    setCopied(null);
    try {
      const link = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(link, '_blank', 'noopener,noreferrer');
      await trackShare('whatsapp_group');
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  }

  // Facebook share dialog. The user manually picks the destination
  // (timeline / a specific group / a story). We ALSO pre-copy the
  // formatted text to the clipboard so the user can paste a caption
  // since Facebook's sharer dialog only takes the URL.
  async function onShareFacebookGroup() {
    setStatus('');
    setCopied(null);
    try {
      // Best-effort caption copy (silent — no toast unless it succeeded
      // visibly via the secondary button row).
      try { await copyToClipboard(shareText); } catch { /* ignore */ }
      const link = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
      window.open(link, '_blank', 'noopener,noreferrer');
      await trackShare('facebook_group');
      toast.success(t('adshar.captionCopiedHint'));
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  }

  // Compact variant (single share button)
  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={onNativeShare}
        className="ad-share-btn-compact"
        data-testid="share-ad-compact"
        title={t('adshar.share')}
      >
        <Share2 size={20} />
      </button>
    );
  }

  // Full variant (all buttons)
  return (
    <div className="ad-share-container" data-testid="ad-share">
      <button
        type="button"
        onClick={onNativeShare}
        className="ad-share-btn-primary"
        data-testid="share-ad-native"
      >
        <Share2 size={18} />
        <span>{t('adshar.share')}</span>
      </button>

      {/* iter312 — Social platform secondary row.
          Three platform-specific entry points. Each one is
          USER-TRIGGERED only — no auto-posting, no scraping. */}
      <div className="ad-share-platforms-row" data-testid="ad-share-platforms">
        <button
          type="button"
          onClick={onShareWhatsAppStatus}
          className="ad-share-btn-platform ad-share-btn-wa-status"
          data-testid="share-ad-whatsapp-status"
          aria-label={t('adshar.whatsappStatus')}
        >
          <MessageCircle size={16} />
          <span>{t('adshar.whatsappStatus')}</span>
        </button>

        <button
          type="button"
          onClick={onShareWhatsAppGroup}
          className="ad-share-btn-platform ad-share-btn-wa-group"
          data-testid="share-ad-whatsapp-group"
          aria-label={t('adshar.whatsappGroup')}
        >
          <Users size={16} />
          <span>{t('adshar.whatsappGroup')}</span>
        </button>

        <button
          type="button"
          onClick={onShareFacebookGroup}
          className="ad-share-btn-platform ad-share-btn-fb-group"
          data-testid="share-ad-facebook-group"
          aria-label={t('adshar.facebookGroup')}
        >
          <Facebook size={16} />
          <span>{t('adshar.facebookGroup')}</span>
        </button>
      </div>

      <div className="ad-share-secondary-row">
        <button
          type="button"
          onClick={onCopyLink}
          className={`ad-share-btn-secondary ${copied === 'link' ? 'copied' : ''}`}
          data-testid="copy-ad-link"
        >
          {copied === 'link' ? <Check size={16} /> : <Link2 size={16} />}
          <span>{t('adshar.copyLink')}</span>
        </button>

        <button
          type="button"
          onClick={onCopyText}
          className={`ad-share-btn-secondary ${copied === 'text' ? 'copied' : ''}`}
          data-testid="copy-ad-text"
        >
          {copied === 'text' ? <Check size={16} /> : <FileText size={16} />}
          <span>{t('adshar.copyText')}</span>
        </button>
      </div>

      {/* NOTE: This feature intentionally does NOT:
          - Auto post anywhere
          - Bulk post
          - Scrape FB groups
          - Read WhatsApp/Facebook content
          - Promise direct WhatsApp Status placement
      */}
    </div>
  );
}

export default AdShare;
